/**
 * Super-Admin → Tenant Users dashboard (iter317).
 *
 * Layout chosen by user: aggregate platform-wide cards on top
 * + per-tenant table below. Filter by tenant.
 *
 * Reads `/api/super-admin/tenants/user-counts` which returns:
 *   {
 *     roles: ["admin","staff","coach","parent","customer","player","other","all"],
 *     platform_totals: { admin:{active,total}, staff:{active,total}, ..., all:{active,total} },
 *     tenants: [{ tenant_id, tenant_name, tenant_slug, counts: {role: {active,total}, ...} }],
 *   }
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Search, Users, ShieldCheck, GraduationCap, UserCircle2, Building2 } from "lucide-react";

const API =
  (typeof window !== "undefined" && window.location
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL) + "/api";

const authHeader = () => {
  const token =
    localStorage.getItem("token") || localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ROLE_LABELS = {
  admin: "Admins",
  staff: "Staff",
  coach: "Coaches",
  parent: "Parents",
  customer: "Customers",
  player: "Players",
  other: "Other",
  all: "All Users",
};

const ROLE_ICONS = {
  admin: ShieldCheck,
  staff: Users,
  coach: GraduationCap,
  parent: UserCircle2,
  customer: Users,
  player: Users,
  other: Users,
  all: Building2,
};

export default function SuperAdminTenantUsers() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/super-admin/tenants/user-counts`, {
        headers: authHeader(),
      });
      setData(res.data);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to load tenant user counts",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredTenants = useMemo(() => {
    if (!data?.tenants) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.tenants;
    return data.tenants.filter(
      (t) =>
        (t.tenant_name || "").toLowerCase().includes(q) ||
        (t.tenant_slug || "").toLowerCase().includes(q) ||
        (t.tenant_id || "").toLowerCase().includes(q),
    );
  }, [data, search]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        data-testid="super-admin-tenant-users-loading"
      >
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-slate-500" data-testid="super-admin-tenant-users-empty">
        Unable to load tenant user counts.
      </div>
    );
  }

  const { roles, platform_totals, tenants } = data;
  // Card order: skip "all" + "other" (shown separately / hidden); keep
  // primary user types as colored cards.
  const cardRoles = roles.filter((r) => r !== "other" && r !== "all");

  return (
    <div className="space-y-8" data-testid="super-admin-tenant-users">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Tenant Users</h1>
        <p className="text-sm text-slate-500 mt-1">
          Platform-wide user counts by role + per-tenant breakdown. Active /
          Total. Useful for billing-tier reporting and volume sanity-checks.
        </p>
      </header>

      {/* Platform-wide aggregate cards */}
      <section
        className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-7"
        data-testid="platform-totals-grid"
      >
        {cardRoles.map((role) => {
          const Icon = ROLE_ICONS[role] || Users;
          const counts = platform_totals[role] || { active: 0, total: 0 };
          return (
            <div
              key={role}
              className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors"
              data-testid={`platform-card-${role}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wide text-slate-500 font-medium">
                  {ROLE_LABELS[role] || role}
                </span>
                <Icon className="w-4 h-4 text-slate-400" />
              </div>
              <div className="text-2xl font-semibold text-slate-900">
                {counts.active.toLocaleString()}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {counts.total !== counts.active && (
                  <span>{counts.total.toLocaleString()} total</span>
                )}
                {counts.total === counts.active && <span>active</span>}
              </div>
            </div>
          );
        })}
        {/* Total users card spans across as the closing summary */}
        <div
          className="bg-slate-900 text-white rounded-lg p-4 lg:col-span-1"
          data-testid="platform-card-all"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
              {ROLE_LABELS.all}
            </span>
            <Building2 className="w-4 h-4 text-slate-300" />
          </div>
          <div className="text-2xl font-semibold">
            {(platform_totals.all?.active || 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-300 mt-0.5">
            {(platform_totals.all?.total || 0).toLocaleString()} total
          </div>
        </div>
      </section>

      {/* Per-tenant table */}
      <section
        className="bg-white border border-slate-200 rounded-lg overflow-hidden"
        data-testid="per-tenant-table-section"
      >
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-4">
          <h2 className="font-semibold text-slate-800">By Tenant</h2>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <input
              type="text"
              placeholder="Filter by tenant name, slug, or id…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="tenant-filter-input"
              className="pl-7 pr-3 py-1.5 text-sm border border-slate-300 rounded w-72"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Tenant</th>
                {cardRoles.map((r) => (
                  <th
                    key={r}
                    className="px-3 py-2 text-right font-medium whitespace-nowrap"
                  >
                    {ROLE_LABELS[r] || r}
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-medium">
                  {ROLE_LABELS.all}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredTenants.length === 0 && (
                <tr>
                  <td
                    colSpan={cardRoles.length + 2}
                    className="px-4 py-8 text-center text-slate-500"
                    data-testid="per-tenant-empty"
                  >
                    No tenants match the current filter.
                  </td>
                </tr>
              )}
              {filteredTenants.map((t) => (
                <tr
                  key={t.tenant_id || "_unassigned"}
                  className="hover:bg-slate-50"
                  data-testid={`tenant-row-${t.tenant_id || "unassigned"}`}
                >
                  <td className="px-4 py-2 font-medium text-slate-800">
                    <div>{t.tenant_name}</div>
                    {t.tenant_slug && (
                      <div className="text-xs text-slate-500 font-normal">
                        {t.tenant_slug}
                      </div>
                    )}
                  </td>
                  {cardRoles.map((r) => {
                    const c = t.counts[r] || { active: 0, total: 0 };
                    return (
                      <td
                        key={r}
                        className="px-3 py-2 text-right font-mono tabular-nums"
                        title={`${c.active} active of ${c.total} total`}
                      >
                        {c.active === c.total ? (
                          <span className="text-slate-800">
                            {c.total.toLocaleString()}
                          </span>
                        ) : (
                          <span>
                            <span className="text-slate-900 font-semibold">
                              {c.active.toLocaleString()}
                            </span>
                            <span className="text-slate-400">
                              {" "}
                              / {c.total.toLocaleString()}
                            </span>
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-slate-900">
                    {(t.counts.all?.active || 0).toLocaleString()}
                    {(t.counts.all?.total || 0) !==
                      (t.counts.all?.active || 0) && (
                      <span className="text-slate-400 font-normal">
                        {" "}
                        / {(t.counts.all?.total || 0).toLocaleString()}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-slate-200 text-xs text-slate-500 bg-slate-50">
          Active / Total. {tenants.length} tenant{tenants.length === 1 ? "" : "s"} ·{" "}
          showing {filteredTenants.length}
        </div>
      </section>
    </div>
  );
}
